<script setup lang="ts">
/**
 * /login —— 运营端登录(S-10;版式按 v2 · O8 重排,S-41)。
 *
 * 在 S-10 之前这个前端靠一个硬编码的 'dev-ops-token' 直接调运营端接口,
 * 没有登录这回事。现在没有令牌就到这里来。
 *
 * 页面只做一件事:账号密码换令牌。**失败的话照抄服务端那句** ——
 * 它的 401 故意不区分「用户名不存在」与「密码不对」(区分了就是一台账号枚举器),
 * 前端在这里编一句更友好的话反而会把那条线抹掉。
 *
 * ═══ 这一页为什么自带 LoginLayout ═══
 *
 * O8 的文案是**整页**的:品牌区的端名、副标题、三条要点,登录卡的标题、说明、
 * 两个字段与页脚。它们全部住在这个文件里(向下传给 LoginLayout 的 props),
 * 于是 design-copy 的那一帧 surfaces 只需要指向一个文件 —— 文案只有一处来源,
 * 换版式只在 LoginLayout 里发生,换文案只在这里。
 */
import { ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { NButton, NCollapse, NCollapseItem, NForm, NFormItem, NInput } from 'naive-ui'
import { ErrorBanner, LoginLayout } from '@sea-drone/ui'
import { api, ApiError, setToken } from '../api/client'

/** O8 品牌区底端的三条要点(画板逐字) */
const POINTS = ['订单、结算、承运商一个后台管完', '每一次改价都建新版本,可回溯', '操作全部进审计流水']

const router = useRouter()
const route = useRoute()

const username = ref('')
const password = ref('')
const busy = ref(false)
const error = ref<string | null>(null)

async function submit(): Promise<void> {
  if (username.value.trim() === '' || password.value === '') {
    error.value = '账号和密码都要填'
    return
  }
  busy.value = true
  error.value = null
  try {
    const result = await api.login(username.value.trim(), password.value)
    setToken(result.token)
    const redirect = typeof route.query.redirect === 'string' ? route.query.redirect : '/orders'
    await router.push(redirect)
  } catch (e) {
    // 401 也会走到这里(api 层会顺手清掉旧令牌并留在本页,因为已经在 /login 上)
    error.value =
      e instanceof ApiError
        ? `[${e.code}] ${e.message}(requestId ${e.requestId})`
        : e instanceof Error
          ? e.message
          : String(e)
  } finally {
    busy.value = false
  }
}
</script>

<template>
  <LoginLayout
    tone="ops-dark"
    product="运营后台"
    tagline="面向平台运营的日常作业界面。"
    :points="POINTS"
  >
    <h1 class="card-title">登录运营后台</h1>
    <p class="card-note">账号由平台运营开通,不支持自助注册</p>

    <ErrorBanner v-if="error" class="banner" tone="ops-dark" :message="error" title="登录失败" />

    <n-form class="form" @submit.prevent="submit">
      <n-form-item label="账号">
        <n-input v-model:value="username" size="large" placeholder="运营账号" @keyup.enter="submit" />
      </n-form-item>
      <n-form-item label="密码">
        <n-input
          v-model:value="password"
          type="password"
          size="large"
          show-password-on="click"
          placeholder="密码"
          @keyup.enter="submit"
        />
      </n-form-item>
      <n-button type="primary" size="large" block :loading="busy" @click="submit">登录</n-button>
    </n-form>

    <!--
      这段说明是给第一次接手的人看的:第一个账号从哪来、令牌活多久。
      收进折叠里,默认不展开 —— 天天登录的人不需要每次读一遍。
      (O8 画板上没有它;画板画的是理想空态,不构成「把它删掉」的指令 —— 见 AS-086)
    -->
    <n-collapse class="hint">
      <n-collapse-item title="首次登录与令牌说明" name="first-login">
        <p class="hint-text">
          第一个运营账号由服务启动时按 <code>OPS_BOOTSTRAP_USERNAME</code> /
          <code>OPS_BOOTSTRAP_PASSWORD</code> 建出来;加人用
          <code>node scripts/create-ops-account.mjs &lt;用户名&gt;</code>。
          令牌 12 小时过期,过期后回到这一页。
        </p>
      </n-collapse-item>
    </n-collapse>

    <p class="card-foot">登录即表示你已知悉数据使用范围</p>
  </LoginLayout>
</template>

<style scoped>
.card-title {
  margin: 0;
  font-size: 22px;
  font-weight: 700;
  color: var(--sd-text1);
}

.card-note {
  margin: 8px 0 0;
  font-size: 13px;
  color: var(--sd-text3);
}

.banner {
  margin-top: 16px;
}

.form {
  margin-top: 16px;
}

/* 输入框的底比登录卡再深一档(画板:输入框底 = 页面底色,卡是面板色)。
   这一处在主题层解决(packages/ui/src/theme.ts 的 opsDarkThemeOverrides)——
   naive-ui 的主题变量默认内联挂在元素上,视图里 `:deep` 写 CSS 变量覆盖不动它,
   写了会是一段**不报错也不生效**的死样式 */

.hint {
  margin-top: 16px;
}

.hint-text {
  margin: 0;
  font-size: 12.5px;
  line-height: 1.7;
}

/* 页脚说明:画板逐字,居中小字 */
.card-foot {
  margin: 16px 0 0;
  text-align: center;
  font-size: 12px;
  color: var(--sd-text3);
}
</style>
